// 共通定数
import { MyConfig as mc } from '@/MyConfig'
// Vuexストア
import MyStore from '@/store'
// Cognitoのライブラリ
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute, CognitoRefreshToken } from 'amazon-cognito-identity-js'

/* *************************************************************************************************
 * クラス MyAuth
 *   Cognitoに対して認証処理を行う
 *   amazon-cognito-identityライブラリがcallback形式のため、promiseオブジェクトとしてラップ
 *   ↓↓↓★セキュリティの観点から見い直す必要あり↓↓↓
 *   ユーザ名・各トークン情報は、ページ間(Vueコンポーネント間)で共有できるようにVuexストアに登録
 *   Vuexストアはリロードで消えるため、モジュールvuex-persistedstateでセッションストレージにも保存
 *   なお、amazon-cognito-identityライブラリの仕様でローカルストレージにトークン情報を保存する模様
 *   ↑↑↑セキュリティの観点から見い直す必要あり↑↑↑
* ********************************************************************************************** */
class MyAuth {
  /* ===================================================================================
   * コンストラクタ
   * ================================================================================ */
  constructor () {
    this.setupCognitoUserPool()
  }

  /* ===================================================================================
   * メソッド setupCognitoUserPool
   *   本クラス内で共通で使用するCognitoのUserPoolオブジェクトを生成する
   * ================================================================================ */
  setupCognitoUserPool () {
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.setupCognitoUserPool: Start', mc.LOG_TYPE_I) : (() => { })()
    this.cognitoUserPool = new CognitoUserPool({
      UserPoolId: mc.AWS_USER_POOL_ID,
      ClientId: mc.AWS_CLIENT_ID
    })
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.setupCognitoUserPool: End', mc.LOG_TYPE_I) : (() => { })()
  }

  /* ===================================================================================
   * signUpStart
   *   サインナップを開始
   * ================================================================================ */
  signUpStart (email, username, password) {
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signUpStart: Start', mc.LOG_TYPE_I) : (() => { })()
    const dataEmail = { Name: 'email', Value: email }
    const attributeList = []
    attributeList.push(new CognitoUserAttribute(dataEmail))
    return new Promise((resolve, reject) => {
      this.cognitoUserPool.signUp(username, password, attributeList, null, (err, result) => {
        if (err) {
          mc.LOG_LEVEL ? console.log('%c error: Module=MyAuth.signUpStart: ERROR:', mc.LOG_TYPE_E, err) : (() => { })()
          reject(err)
        } else {
          mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signUpStart: OK', mc.LOG_TYPE_I) : (() => { })()
          resolve(result)
        }
      })
    })
  }

  /* ===================================================================================
   * signUpFix
   *   サインアップを完了
   * ================================================================================ */
  signUpFix (username, confirmationCode) {
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signUpFix: Start', mc.LOG_TYPE_I) : (() => { })()
    const userData = { Username: username, Pool: this.cognitoUserPool }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) {
          mc.LOG_LEVEL ? console.log('%c error: Module=MyAuth.signUpFix: ERROR:', mc.LOG_TYPE_E, err) : (() => { })()
          reject(err)
        } else {
          mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signUpFix: OK', mc.LOG_TYPE_I) : (() => { })()
          resolve(result)
        }
      })
    })
  }

  /* ===================================================================================
   * メソッド signOut
   *   サインアウト処理を行う
   *   Cognitoへのサインアウトと、Vuexストア内・セッションストレージ内の情報消去も行う
   *   ローカルストレージ内の情報はamazon-cognito-identityが消去
   * ================================================================================ */
  signOut () {
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signOut: Start', mc.LOG_TYPE_I) : (() => { })()
    // Cognitoに対してサインアウト処理を実行
    this.cognitoUserPool.getCurrentUser().signOut()
    // Vuexストアから認証情報を削除
    MyStore.commit('MyAuth/setAuthInfo', { username: '', idTokenRaw: '', accessTokenRaw: '', refreshTokenRaw: '' })
    // Vuexストアからセッションストレージに保存していた認証情報を削除
    sessionStorage.clear()
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signOut: End', mc.LOG_TYPE_I) : (() => { })()
  }

  /* ===================================================================================
   * メソッド signIn
   *   ユーザ名・パスワードをCognitoに送信してサインイン処理を行う
   *   OKであればVuexストアにユーザ名と各トークンを含む情報(..TokenRaw)を登録
   * ================================================================================ */
  signIn (username, password) {
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signIn: Start', mc.LOG_TYPE_I) : (() => { })()
    const authenticationData = { Username: username, Password: password }
    const authenticationDetails = new AuthenticationDetails(authenticationData)
    const userData = { Username: username, Pool: this.cognitoUserPool }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      // Cognitoに対してサインイン処理を実行
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const idTokenRaw = result.idToken
          const accessTokenRaw = result.accessToken
          const refreshTokenRaw = result.refreshToken
          const idToken = idTokenRaw.jwtToken
          // Vuexストアに登録
          MyStore.commit('MyAuth/setAuthInfo', { username: username, idTokenRaw: idTokenRaw, accessTokenRaw: accessTokenRaw, refreshTokenRaw: refreshTokenRaw })
          mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signIn: OK', mc.LOG_TYPE_I) : (() => { })()
          resolve({ status: 'OK', content: { username: username, idToken: idToken } })
        },
        onFailure: (err) => {
          // 一般的なユーザ名・パスワード不一致であればrejectせずにresolveする
          if (err.code === 'NotAuthorizedException') {
            mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.signIn: NOAUTH:', mc.LOG_TYPE_I, err) : (() => { })()
            resolve({ status: 'NOAUTH', content: err.message })
          } else {
            // ほかはrejectする(本モジュールのコール元で例外エラーとしてcatchされる
            mc.LOG_LEVEL ? console.log('%c error: Module=MyAuth.signIn: ERROR:', mc.LOG_TYPE_E, err) : (() => { })()
            reject(err)
          }
        }
      })
    })
  }

  /* ===================================================================================
   * メソッド checkSession
   *   Vuexストアに登録されているユーザ名・各トークン情報を使って認証状態を確認
   *   IDトークンの有効期限が切れていなければOK、
   *   IDトークンの有効期限が切れていてもRefreshトークンで更新できればOK
   *   更新後は、Vuexストア上の各トークン情報(..TokenRaw)も更新
   * ================================================================================ */
  checkSession () {
    mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.checkSession: Start', mc.LOG_TYPE_I) : (() => { })()
    const idTokenRaw = MyStore.state.MyAuth.idTokenRaw
    const accessTokenRaw = MyStore.state.MyAuth.accessTokenRaw
    const refreshTokenRaw = MyStore.state.MyAuth.refreshTokenRaw
    return new Promise((resolve, reject) => {
      if (!idTokenRaw || !accessTokenRaw || !refreshTokenRaw) {
        mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.checkSession: NOAUTH:', mc.LOG_TYPE_I) : (() => { })()
        resolve({ status: 'NOAUTH', content: 'No valid credentials found. Please sign in.' })
      } else {
        const username = accessTokenRaw.payload.username
        const idToken = idTokenRaw.jwtToken
        const refreshToken = refreshTokenRaw.token
        const exp = idTokenRaw.payload.exp * 1000
        const now = new Date().getTime()
        const remainingTime = exp - now
        mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.checkSession: remainingTime:', mc.LOG_TYPE_I, remainingTime) : (() => { })()
        // IDトークンの有効期限が切れていなければ本処理を終了
        if (remainingTime > 0) {
          resolve({ status: 'OK', content: { username: username, idToken: idToken } })
        // IDトークンの有効期限が切れていれば更新処理に進む
        } else {
          const userData = { Username: username, Pool: this.cognitoUserPool }
          const cognitoUser = new CognitoUser(userData)
          const token = new CognitoRefreshToken({ RefreshToken: refreshToken })
          // Cognitoに対してトークン情報更新処理を実行
          cognitoUser.refreshSession(token, (err, session) => {
            if (err) {
              mc.LOG_LEVEL ? console.log('%c error: Module=MyAuth.checkSession: ERROR:', mc.LOG_TYPE_E, err) : (() => { })()
              reject(err)
            } else {
              // セッションが無効な場合はrejectせずにresolveする
              if (!session.isValid()) {
                mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.checkSession: NOAUTH(invalid session):', mc.LOG_TYPE_I, session) : (() => { })()
                resolve({ status: 'NOAUTH', content: 'No valid credentials found. Please sign in.' })
              } else {
                const newIdTokenRaw = session.idToken
                const newAccessTokenRaw = session.accessToken
                const newRefreshTokenRaw = session.refreshToken
                const newIdToken = newIdTokenRaw.jwtToken
                // Vuexストアに登録
                MyStore.commit('MyAuth/setAuthInfo', { username: username, idTokenRaw: newIdTokenRaw, accessTokenRaw: newAccessTokenRaw, refreshTokenRaw: newRefreshTokenRaw })
                mc.LOG_LEVEL ? console.log('%c info: Module=MyAuth.checkSession: OK', mc.LOG_TYPE_I) : (() => { })()
                resolve({ status: 'OK', content: { username: username, idToken: newIdToken } })
              }
            }
          })
        }
      }
    })
  }

  /* ===================================================================================
   * passwdChange
   * パスワード変更
   * ================================================================================ */
  passwdChange (oldPassword, newPassword) {
    const cognitoUser = this.cognitoUserPool.getCurrentUser()
    return new Promise((resolve, reject) => {
      if (!cognitoUser) {
        let err = new Error()
        err = { nextAction: 'signIn' }
        reject(err)
      } else {
        cognitoUser.getSession((err, session) => {
          if (err) {
            reject(err)
          } else {
            if (!session.isValid()) {
              reject(session)
            } else {
              cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
                if (err) {
                  reject(err)
                } else {
                  resolve(result)
                }
              })
            }
          }
        })
      }
    })
  }

  /* ===================================================================================
   * attributeGet
   * 属性情報を取得
   * ================================================================================ */
  attributeGet (username) {
    const userData = { Username: username, Pool: this.cognitoUserPool }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        } else {
          if (!session.isValid()) {
            reject(session)
          } else {
            cognitoUser.getUserAttributes((err, result) => {
              if (err) {
                reject(err)
              } else {
                resolve(result)
              }
            })
          }
        }
      })
    })
  }
}

export { MyAuth }
